<mat-card style="margin-top: 30px">
  <mat-card-header>
    <div class="statistics-header">
      <h2 mat-card-title><mat-icon>bar_chart</mat-icon>Migration Statistics</h2>
      <div class="statistics-header-right">
        @if (timelineViewEnabled) {
          <button
            mat-mini-fab
            color="primary"
            aria-label="Download timeline chart"
            [disabled]="timelineChartItems.length === 0"
            (click)="exportTimelineChart()"
          >
            <mat-icon>download</mat-icon>
          </button>
        }
        <mat-slide-toggle
          color="primary"
          [ngModel]="timelineViewEnabled"
          (ngModelChange)="onTimelineViewChanged($event)"
        >
          Timeline View
        </mat-slide-toggle>
      </div>
    </div>
  </mat-card-header>
  <mat-card-content>
    @if (!(hasAdminGroup$ | async)) {
      <div class="statistics-empty-state">
        <mat-icon class="statistics-empty-icon">lock</mat-icon>
        <h3>Access restricted</h3>
        <p>You do not have permission to view migration statistics.</p>
      </div>
    } @else if (timelineViewEnabled) {
      <div class="timeline-toolbar">
        <mat-form-field appearance="outline" class="timeline-range-preset">
          <mat-label>Quick Select</mat-label>
          <mat-select
            [(ngModel)]="selectedTimelineRangePreset"
            (selectionChange)="onTimelineRangePresetChanged($event.value)"
          >
            <mat-option [value]="null">Custom Range</mat-option>
            <mat-option [value]="'last24h'">Last 24 Hours</mat-option>
            <mat-option [value]="'last2days'">Last 2 Days</mat-option>
            <mat-option [value]="'lastweek'">Last Week</mat-option>
          </mat-select>
        </mat-form-field>
        <div class="timeline-date-time-group">
          <mat-form-field appearance="outline" class="timeline-date-field">
            <mat-label>Start Date</mat-label>
            <input
              matInput
              [matDatepicker]="timelineStartDatePicker"
              [disabled]="!isCustomTimelineRange"
              [(ngModel)]="selectedTimelineStartDate"
              (ngModelChange)="onTimelineDateRangeChanged()"
            />
            <mat-datepicker-toggle
              matIconSuffix
              [disabled]="!isCustomTimelineRange"
              [for]="timelineStartDatePicker"
            ></mat-datepicker-toggle>
            <mat-datepicker
              #timelineStartDatePicker
              [disabled]="!isCustomTimelineRange"
              startView="month"
            ></mat-datepicker>
          </mat-form-field>
          <mat-form-field appearance="outline" class="timeline-time-field">
            <mat-label>Hours</mat-label>
            <input
              matInput
              type="number"
              min="0"
              max="23"
              [disabled]="!isCustomTimelineRange"
              [(ngModel)]="startTimeHours"
              (ngModelChange)="onTimelineDateRangeChanged()"
            />
          </mat-form-field>
        </div>
        <div class="timeline-date-time-group">
          <mat-form-field appearance="outline" class="timeline-date-field">
            <mat-label>End Date</mat-label>
            <input
              matInput
              [matDatepicker]="timelineEndDatePicker"
              [disabled]="!isCustomTimelineRange"
              [(ngModel)]="selectedTimelineEndDate"
              (ngModelChange)="onTimelineDateRangeChanged()"
            />
            <mat-datepicker-toggle
              matIconSuffix
              [disabled]="!isCustomTimelineRange"
              [for]="timelineEndDatePicker"
            ></mat-datepicker-toggle>
            <mat-datepicker
              #timelineEndDatePicker
              [disabled]="!isCustomTimelineRange"
              startView="month"
            ></mat-datepicker>
          </mat-form-field>
          <mat-form-field appearance="outline" class="timeline-time-field">
            <mat-label>Hours</mat-label>
            <input
              matInput
              type="number"
              min="0"
              max="23"
              [disabled]="!isCustomTimelineRange"
              [(ngModel)]="endTimeHours"
              (ngModelChange)="onTimelineDateRangeChanged()"
            />
          </mat-form-field>
        </div>
        <div class="timeline-summary">
          <span>{{ timelineMigrations.length }} migration(s)</span>
          <span>{{ selectedTimelineDateRangeLabel }}</span>
        </div>
      </div>

      @if (loadingMigrations) {
        <div class="statistics-empty-state">
          <mat-icon class="statistics-empty-icon">hourglass_top</mat-icon>
          <h3>Loading timeline</h3>
          <p>Please wait...</p>
        </div>
      } @else if (timelineChartItems.length > 0) {
        <div class="timeline-chart-card">
          <div class="chart-container" [style.height.px]="timelineChartHeight">
            <div
              class="chart-canvas-wrapper"
              [style.height.px]="timelineChartHeight"
            >
              <canvas
                baseChart
                [type]="timelineBarChartType"
                [data]="timelineBarChartData"
                [options]="timelineBarChartOptions"
              ></canvas>
            </div>
          </div>
        </div>
      } @else {
        <div class="statistics-empty-state timeline-empty-state">
          <mat-icon class="statistics-empty-icon">insights</mat-icon>
          <h3>No timeline data</h3>
          <p>No migrations are available for the selected day.</p>
        </div>
      }
    } @else {
      <mat-tab-group (selectedTabChange)="onTabChange($event)">
        <mat-tab label="Migrations Statistics">
          <div class="migration-selector-wrapper">
            <mat-form-field class="migration-selector" appearance="outline">
              <mat-label>Select Migration</mat-label>
              <mat-select
                [(ngModel)]="selectedMigrationId"
                (selectionChange)="onMigrationSelectionChanged()"
                [disabled]="loadingMigrations"
              >
                <mat-select-trigger>
                  @if (selectedMigrationId !== null) {
                    {{ getSelectedMigrationDisplayLabel() }}
                  } @else {
                    -- None --
                  }
                </mat-select-trigger>
                <mat-option [value]="null">-- None --</mat-option>
                @if (migrations.length > 0) {
                  @for (migration of migrations; track migration.id) {
                    <mat-option [value]="migration.id">
                      <div class="migration-option">
                        <div class="migration-option-title">
                          {{ getMigrationDisplayLabel(migration) }}
                        </div>
                        <div class="migration-option-status">
                          Status: {{ migration.status }}
                        </div>
                      </div>
                    </mat-option>
                  }
                } @else {
                  <mat-option disabled>No migrations available</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          <div
            class="statistics-summary-container"
            [class.statistics-summary-container-hidden]="
              !migrationId ||
              !migration ||
              error ||
              !migration.statistics_started_local
            "
            [attr.aria-hidden]="
              !migrationId ||
              !migration ||
              error ||
              !migration.statistics_started_local
            "
          >
            <div class="statistic-item">
              <mat-icon color="primary">info</mat-icon>
              <span class="statistic-label">Status:</span>
              <span class="statistic-value">
                {{ migration?.status ?? "" }}
              </span>
            </div>
            <div class="statistic-item">
              <mat-icon color="primary">play_circle</mat-icon>
              <span class="statistic-label">Studies:</span>
              <span class="statistic-value">
                {{ migration?.statistics_obj?.no_of_protocols ?? "" }}
              </span>
            </div>
             <div class="statistic-item">
              <mat-icon color="queue">queue</mat-icon>
              <span class="statistic-label">Queued:</span>
              <span class="statistic-value">
                {{ migration?.created_dt_local || "" }}
              </span>
            </div>
            <div class="statistic-item">
              <mat-icon color="accent">schedule</mat-icon>
              <span class="statistic-label">Started:</span>
              <span class="statistic-value">
                {{ migration?.statistics_started_local || "" }}
              </span>
            </div>
            <div class="statistic-item">
              <mat-icon color="primary">task_alt</mat-icon>
              <span class="statistic-label">Ended:</span>
              <span class="statistic-value">
                {{ migration?.statistics_ended_local || "" }}
              </span>
            </div>
            <div class="statistic-item">
              <mat-icon color="primary">timer</mat-icon>
              <span class="statistic-label">Duration:</span>
              <span class="statistic-value">
                {{ migration?.durationText || "" }}
              </span>
            </div>
            <div class="statistic-item statistics-action-item">
              <button
                mat-mini-fab
                color="primary"
                aria-label="Download statistics"
                [disabled]="
                  !migrationId ||
                  !migration ||
                  error ||
                  !migration.statistics_started_local ||
                  statisticsRows.length === 0
                "
                (click)="exportStatistics()"
              >
                <mat-icon>download</mat-icon>
              </button>
            </div>
          </div>

          <div class="statistics-tab-content">
            @if (!migrationId) {
              <div class="statistics-empty-state">
                <mat-icon class="statistics-empty-icon">info</mat-icon>
                <h3>No migration selected</h3>
                <p>Select a migration from the dropdown to view statistics.</p>
              </div>
            } @else if (error) {
              <div class="statistics-empty-state">
                <mat-icon class="statistics-empty-icon">error</mat-icon>
                <h3>Unable to load statistics</h3>
                <p>Please try again later.</p>
              </div>
            } @else {
              @if (statisticsRows.length > 0) {
                <div class="statistics-filter-row">
                  @if (migrationsViewMode !== "diagram") {
                    <mat-form-field class="name-filter" appearance="outline">
                      <mat-label>Name</mat-label>
                      <mat-select
                        [(ngModel)]="selectedNameFilter"
                        (selectionChange)="onNameFilterChanged()"
                        [disabled]="loading || statisticsRows.length === 0"
                      >
                        <mat-option [value]="null">All</mat-option>
                        @for (name of nameFilterOptions; track name) {
                          <mat-option [value]="name">{{ name }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }

                  <mat-form-field
                    class="protocol-id-filter"
                    appearance="outline"
                  >
                    <mat-label>Protocol ID</mat-label>
                    <mat-select
                      [(ngModel)]="selectedProtocolIdFilter"
                      (selectionChange)="onProtocolIdFilterChanged()"
                      [disabled]="loading || statisticsRows.length === 0"
                    >
                      <mat-option [value]="null">All</mat-option>
                      @for (
                        protocolId of protocolIdFilterOptions;
                        track protocolId
                      ) {
                        <mat-option [value]="protocolId">{{
                          protocolId
                        }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>

                  @if (migrationsViewMode === 'diagram' && initializingMigrationDuration !== null) {
                    <div class="initializing-migration-stat">
                      <mat-icon color="primary">rocket_launch</mat-icon>
                      <span class="initializing-migration-label">Initializing Migration:</span>
                      <span class="initializing-migration-value">{{ initializingMigrationDurationText }}</span>
                    </div>
                  }

                  <mat-slide-toggle
                    class="statistics-view-toggle"
                    [checked]="migrationsViewMode === 'diagram'"
                    (change)="onMigrationsViewToggle($event.checked)"
                    [disabled]="loading"
                  >
                    Diagram View
                  </mat-slide-toggle>
                </div>
              }

              @if (migrationsViewMode === "table") {
                <div class="statistics-table-container">
                  @if (loading) {
                    <div class="statistics-empty-state">
                      <mat-icon class="statistics-empty-icon"
                        >hourglass_top</mat-icon
                      >
                      <h3>Loading statistics</h3>
                      <p>Please wait...</p>
                    </div>
                  } @else if (statisticsRows.length > 0) {
                    <app-table-grid
                      [data]="statisticsRowsPaginated"
                      [displayedColumns]="statisticsColumns"
                      [headers]="statisticsTableHeaders"
                      [columnWidths]="statisticsColumnWidths"
                      [isLoading]="loading"
                      [skeletonRowsCount]="statisticsPaginatorFilter.pageSize"
                      [enableSorting]="true"
                      [columnSortTypes]="columnSortTypes"
                      (sortChanged)="onSortChanged($event)"
                    ></app-table-grid>
                    <mat-paginator
                      showFirstLastButtons="true"
                      [length]="statisticsPaginatorFilter.count"
                      [pageIndex]="statisticsPaginatorFilter.currentPage"
                      [pageSize]="statisticsPaginatorFilter.pageSize"
                      [pageSizeOptions]="[5, 10, 25, 100]"
                      (page)="paginateStatistics($event)"
                      class="mat-elevation-z8"
                    >
                    </mat-paginator>
                  } @else if (statisticsLoadCompleted) {
                    <div class="statistics-empty-state">
                      <mat-icon class="statistics-empty-icon"
                        >insights</mat-icon
                      >
                      <h3>No statistics for this migration</h3>
                      <p>There are no statistics for the selected migration.</p>
                    </div>
                  }
                </div>
              } @else {
                <div class="diagram-tab-content">
                  @if (!migration || !migration.statistics_obj) {
                    <div class="statistics-empty-state">
                      <mat-icon class="statistics-empty-icon">info</mat-icon>
                      <h3>No statistics available</h3>
                      <p>
                        Timeline diagram will appear once migration statistics
                        are loaded.
                      </p>
                    </div>
                  } @else if (diagramChartData && diagramChartData.length > 0) {
                    <div class="chart-container">
                      <div class="chart-canvas-wrapper">
                        <canvas
                          baseChart
                          [type]="barChartType"
                          [data]="barChartData"
                          [options]="barChartOptions"
                        ></canvas>
                      </div>
                    </div>
                  } @else {
                    <div class="statistics-empty-state">
                      <mat-icon class="statistics-empty-icon"
                        >insights</mat-icon
                      >
                      <h3>No timeline data</h3>
                      <p>No execution steps available for this migration.</p>
                    </div>
                  }
                </div>
              }
            }
          </div>
        </mat-tab>

        <mat-tab label="Deleted Migrations Statistics">
          <div class="migration-selector-wrapper">
            <mat-form-field class="migration-selector" appearance="outline">
              <mat-label>Select Deleted Migration</mat-label>
              <mat-select
                [(ngModel)]="selectedMigrationId"
                (selectionChange)="onMigrationSelectionChanged()"
                [disabled]="loadingMigrations"
              >
                <mat-select-trigger>
                  @if (selectedMigrationId !== null) {
                    {{ getSelectedMigrationDisplayLabel() }}
                  } @else {
                    -- None --
                  }
                </mat-select-trigger>
                <mat-option [value]="null">-- None --</mat-option>
                @if (migrations.length > 0) {
                  @for (migration of migrations; track migration.id) {
                    <mat-option [value]="migration.id">
                      <div class="migration-option">
                        <div class="migration-option-title">
                          {{ getMigrationDisplayLabel(migration) }}
                        </div>
                        <div class="migration-option-status">
                          Status: {{ migration.status }}
                        </div>
                      </div>
                    </mat-option>
                  }
                } @else {
                  <mat-option disabled>No migrations available</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          <div
            class="statistics-summary-container"
            [class.statistics-summary-container-hidden]="
              !migrationId ||
              !migration ||
              error ||
              !migration.statistics_started_local
            "
            [attr.aria-hidden]="
              !migrationId ||
              !migration ||
              error ||
              !migration.statistics_started_local
            "
          >
            <div class="statistic-item">
              <mat-icon color="primary">play_circle</mat-icon>
              <span class="statistic-label">Studies:</span>
              <span class="statistic-value">
                {{ migration?.statistics_obj?.no_of_protocols ?? "" }}
              </span>
            </div>
            <div class="statistic-item">
              <mat-icon color="accent">schedule</mat-icon>
              <span class="statistic-label">Started:</span>
              <span class="statistic-value">
                {{ migration?.statistics_started_local || "" }}
              </span>
            </div>
            <div class="statistic-item">
              <mat-icon color="primary">task_alt</mat-icon>
              <span class="statistic-label">Ended:</span>
              <span class="statistic-value">
                {{ migration?.statistics_ended_local || "" }}
              </span>
            </div>
            <div class="statistic-item">
              <mat-icon color="primary">timer</mat-icon>
              <span class="statistic-label">Duration:</span>
              <span class="statistic-value">
                {{ migration?.durationText || "" }}
              </span>
            </div>
            <div class="statistic-item statistics-action-item">
              <button
                mat-mini-fab
                color="primary"
                aria-label="Download statistics"
                [disabled]="
                  !migrationId ||
                  !migration ||
                  error ||
                  !migration.statistics_started_local ||
                  statisticsRows.length === 0
                "
                (click)="exportStatistics()"
              >
                <mat-icon>download</mat-icon>
              </button>
            </div>
          </div>

          <div class="statistics-tab-content">
            @if (!migrationId) {
              <div class="statistics-empty-state">
                <mat-icon class="statistics-empty-icon">info</mat-icon>
                <h3>No migration selected</h3>
                <p>Select a migration from the dropdown to view statistics.</p>
              </div>
            } @else if (error) {
              <div class="statistics-empty-state">
                <mat-icon class="statistics-empty-icon">error</mat-icon>
                <h3>Unable to load statistics</h3>
                <p>Please try again later.</p>
              </div>
            } @else {
              @if (statisticsRows.length > 0) {
                <div class="statistics-filter-row">
                  @if (deletedMigrationsViewMode !== "diagram") {
                    <mat-form-field class="name-filter" appearance="outline">
                      <mat-label>Name</mat-label>
                      <mat-select
                        [(ngModel)]="selectedNameFilter"
                        (selectionChange)="onNameFilterChanged()"
                        [disabled]="loading || statisticsRows.length === 0"
                      >
                        <mat-option [value]="null">All</mat-option>
                        @for (name of nameFilterOptions; track name) {
                          <mat-option [value]="name">{{ name }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }

                  <mat-form-field
                    class="protocol-id-filter"
                    appearance="outline"
                  >
                    <mat-label>Protocol ID</mat-label>
                    <mat-select
                      [(ngModel)]="selectedProtocolIdFilter"
                      (selectionChange)="onProtocolIdFilterChanged()"
                      [disabled]="loading || statisticsRows.length === 0"
                    >
                      <mat-option [value]="null">All</mat-option>
                      @for (
                        protocolId of protocolIdFilterOptions;
                        track protocolId
                      ) {
                        <mat-option [value]="protocolId">{{
                          protocolId
                        }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>

                  @if (deletedMigrationsViewMode === 'diagram' && initializingMigrationDuration !== null) {
                    <div class="initializing-migration-stat">
                      <mat-icon color="primary">rocket_launch</mat-icon>
                      <span class="initializing-migration-label">Initializing Migration:</span>
                      <span class="initializing-migration-value">{{ initializingMigrationDurationText }}</span>
                    </div>
                  }

                  <mat-slide-toggle
                    class="statistics-view-toggle"
                    [checked]="deletedMigrationsViewMode === 'diagram'"
                    (change)="onDeletedMigrationsViewToggle($event.checked)"
                    [disabled]="loading"
                  >
                    Diagram View
                  </mat-slide-toggle>
                </div>
              }

              @if (deletedMigrationsViewMode === "table") {
                <div class="statistics-table-container">
                  @if (loading) {
                    <div class="statistics-empty-state">
                      <mat-icon class="statistics-empty-icon"
                        >hourglass_top</mat-icon
                      >
                      <h3>Loading statistics</h3>
                      <p>Please wait...</p>
                    </div>
                  } @else if (statisticsRows.length > 0) {
                    <app-table-grid
                      [data]="statisticsRowsPaginated"
                      [displayedColumns]="statisticsColumns"
                      [headers]="statisticsTableHeaders"
                      [columnWidths]="statisticsColumnWidths"
                      [isLoading]="loading"
                      [skeletonRowsCount]="statisticsPaginatorFilter.pageSize"
                      [enableSorting]="true"
                      [columnSortTypes]="columnSortTypes"
                      (sortChanged)="onSortChanged($event)"
                    ></app-table-grid>
                    <mat-paginator
                      showFirstLastButtons="true"
                      [length]="statisticsPaginatorFilter.count"
                      [pageIndex]="statisticsPaginatorFilter.currentPage"
                      [pageSize]="statisticsPaginatorFilter.pageSize"
                      [pageSizeOptions]="[5, 10, 25, 100]"
                      (page)="paginateStatistics($event)"
                      class="mat-elevation-z8"
                    >
                    </mat-paginator>
                  } @else if (statisticsLoadCompleted) {
                    <div class="statistics-empty-state">
                      <mat-icon class="statistics-empty-icon"
                        >insights</mat-icon
                      >
                      <h3>No statistics for this migration</h3>
                      <p>There are no statistics for the selected migration.</p>
                    </div>
                  }
                </div>
              } @else {
                <div class="diagram-tab-content">
                  @if (!migration || !migration.statistics_obj) {
                    <div class="statistics-empty-state">
                      <mat-icon class="statistics-empty-icon">info</mat-icon>
                      <h3>No statistics available</h3>
                      <p>
                        Timeline diagram will appear once migration statistics
                        are loaded.
                      </p>
                    </div>
                  } @else if (diagramChartData && diagramChartData.length > 0) {
                    <div class="chart-container">
                      <div class="chart-canvas-wrapper">
                        <canvas
                          baseChart
                          [type]="barChartType"
                          [data]="barChartData"
                          [options]="barChartOptions"
                        ></canvas>
                      </div>
                    </div>
                  } @else {
                    <div class="statistics-empty-state">
                      <mat-icon class="statistics-empty-icon"
                        >insights</mat-icon
                      >
                      <h3>No timeline data</h3>
                      <p>No execution steps available for this migration.</p>
                    </div>
                  }
                </div>
              }
            }
          </div>
        </mat-tab>
      </mat-tab-group>
    }
  </mat-card-content>
  <mat-card-footer>*statistics overview</mat-card-footer>
</mat-card>
